import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, Spin, Alert, Select, Typography, Table, Tag } from 'antd';
import { API_BASE_URL } from '../../../utils/config';
import { formatMoney, formatNumberTR } from '../../../utils/currency';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import './MaliRaporPage.css';
import { useTheme } from '../../../context/ThemeContext';

const { Title, Text } = Typography;
const { Option } = Select;

function MaliRaporPage() {
    const { maptriksId } = useParams();
    const [searchParams] = useSearchParams();
    const prjId = searchParams.get('prj');
    const { getAuthHeaders } = useAuth();
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [currentYear, setCurrentYear] = useState('2025');
    const { darkMode } = useTheme();
    
    const ARTIS_ORANLARI = {
        2025: 1.0,
        2026: 1.27,
        2027: 1.24 * 1.27,
        2028: 1.19 * 1.24 * 1.27,
        2029: 1.17 * 1.19 * 1.24 * 1.27,
        2030: 1.15 * 1.17 * 1.19 * 1.24 * 1.27
    }
    
    const YILLIK_ENFLASYON = {
        2025: 1.0,
        2026: 1.27,
        2027: 1.24,
        2028: 1.19,
        2029: 1.17,
        2030: 1.15
    };

    useEffect(() => {
        fetchReportData();
    }, [maptriksId, prjId, getAuthHeaders]);
    
    const fetchReportData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const parsedMaptriksId = maptriksId ? parseInt(maptriksId) : null;
            const isValidMaptriksId = parsedMaptriksId !== null && !isNaN(parsedMaptriksId);
            
            if (!prjId && !isValidMaptriksId) {
                setError('Lokasyon ID veya Proje ID gerekli');
                setLoading(false);
                return;
            }
            
            const params = prjId ? { prj: parseInt(prjId) } : { lok: parsedMaptriksId };
            const response = await axios.get(`${API_BASE_URL}/list/mali_rapor`, { 
                params,
                headers: getAuthHeaders()
            });
            
            if (response.data.success) {
                console.log("Mali rapor verisi:", response.data.data);
                setReportData(response.data.data);
            } else {
                setError('Rapor verisi alınamadı');
            }
        } catch (err) {
            console.error('Mali rapor hatası:', err);
            setError(err.response?.data?.detail || 'Rapor yüklenirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };
    
    const updateYearPrediction = () => {
        // Yıl değiştiğinde hesaplamaları güncelle
        // Bu fonksiyon component içinde hesaplamaları yapacak
    };
    
    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }
    
    if (error) {
        return (
            <div style={{ padding: '20px' }}>
                <Alert message="Hata" description={error} type="error" showIcon />
            </div>
        );
    }
    
    if (!reportData) {
        return (
            <div style={{ padding: '20px' }}>
                <Alert message="Bilgi" description="Rapor verisi bulunamadı" type="info" />
            </div>
        );
    }
    
    const {
        lokasyon,
        lokasyon_banka_durum,
        banka_sozlesme_kayit_detaylari,
        gruplanmis_maliyetler,
        kira_gideri_detay_liste,
        toplam_banka_geliri_sozlesme,
        toplam_kabin_geliri_bsk,
        toplam_kira_gideri_hesaplanan,
        toplam_insaat_maliyeti,
        net_kar_zarar_genel,
        net_kira_kar_zarar,
        net_kabin_kar_zarar,
        sozlesme_sayisi,
        yonetim_gideri,
        personel_gideri,
        beklenmeyen_giderler,
        ihale_genel_gideri,
        proje_adi
    } = reportData;
    
    // Net Kira Gelir/Gideri = net_kira_kar_zarar - toplam_kabin_geliri_bsk
    const netKiraKarZararHesaplanan = net_kira_kar_zarar - toplam_kabin_geliri_bsk;
    
    const year = parseInt(currentYear);
    const artisCarpani = ARTIS_ORANLARI[year] || 1;
    
    // Hesaplanmış değerler
    const tahminiKiraGeliri = (toplam_banka_geliri_sozlesme - toplam_kabin_geliri_bsk) * artisCarpani;
    const tahminiKabinGeliri = toplam_kabin_geliri_bsk; 
    const tahminiBankaGeliri = tahminiKiraGeliri + tahminiKabinGeliri;
    const tahminiKiraGideri = toplam_kira_gideri_hesaplanan * artisCarpani;
    const tahminiInsaatMaliyeti = toplam_insaat_maliyeti;
    const tahminiYonetimGideri = yonetim_gideri * sozlesme_sayisi * artisCarpani;
    const tahminiPersonelGideri = personel_gideri * sozlesme_sayisi * artisCarpani;
    const tahminiBeklenmeyen = beklenmeyen_giderler * sozlesme_sayisi * artisCarpani;
    const tahminiIhaleGideri = ihale_genel_gideri;
    
    // Net Kira Gelir/Gideri = (toplam_banka_geliri - kira_gideri) - kabin_geliri
    const tahminiKiraNet = tahminiKiraGeliri - tahminiKiraGideri;
    const tahminiKabinNet = tahminiKabinGeliri - tahminiInsaatMaliyeti;
    const tahminiNetGelir = tahminiBankaGeliri - tahminiKiraGideri - tahminiInsaatMaliyeti 
        - tahminiYonetimGideri - tahminiPersonelGideri - tahminiBeklenmeyen - tahminiIhaleGideri;
    
    const lokasyonAdi = proje_adi || lokasyon?.Name || 'Bilinmeyen Lokasyon';
    const lokasyonId = prjId ? lokasyon?.proje : lokasyon?.MaptriksID;
    
    // Banka grupları (proje bazlı rapor için)
    let bankaGruplari = {};
    if (prjId && banka_sozlesme_kayit_detaylari) {
        banka_sozlesme_kayit_detaylari.forEach(bsk => {
            const bankaAdi = bsk.BankaAdi || 'Bilinmeyen Banka';
            if (!bankaGruplari[bankaAdi]) {
                bankaGruplari[bankaAdi] = {
                    toplam_kira: 0,
                    toplam_kabin: 0,
                    kabin_adedi: 0
                };
            }
            const kiraGeliri = parseFloat(bsk['Kira Bedeli'] || 0);
            const kabinGeliri = parseFloat(bsk['Kabin Bedeli'] || 0);
            bankaGruplari[bankaAdi].toplam_kira += kiraGeliri;
            bankaGruplari[bankaAdi].toplam_kabin += kabinGeliri;
            bankaGruplari[bankaAdi].kabin_adedi++;
        });
        
        // Bankaları toplam gelire göre azalan sırada sırala
        bankaGruplari = Object.fromEntries(
            Object.entries(bankaGruplari).sort((a, b) => {
                const toplamA = a[1].toplam_kira + a[1].toplam_kabin;
                const toplamB = b[1].toplam_kira + b[1].toplam_kabin;
                return toplamB - toplamA;
            })
        );
    }
    
    const bankaColumns = prjId ? [
        { title: 'Sıra', dataIndex: 'sira', key: 'sira', align: 'center', width: 60 },
        { title: 'Banka Adı', dataIndex: 'bankaAdi', key: 'bankaAdi' },
        { title: 'Kabin Adedi', dataIndex: 'kabinAdedi', key: 'kabinAdedi', align: 'center' },
        { title: 'Toplam Kira Geliri', dataIndex: 'toplamKira', key: 'toplamKira', align: 'right', render: (val) => formatMoney(val) },
        { title: 'Toplam Kabin Geliri', dataIndex: 'toplamKabin', key: 'toplamKabin', align: 'right', render: (val) => formatMoney(val) },
        { title: 'Toplam Gelir', dataIndex: 'toplamGelir', key: 'toplamGelir', align: 'right', render: (val) => <p>{formatMoney(val)}</p> }
    ] : [
        { title: 'Banka Adı', dataIndex: 'BankaAdi', key: 'BankaAdi' },
        { title: 'İlgili Kabinler', dataIndex: 'Kabin No', key: 'KabinNo' },
        { title: 'Kira Geliri', dataIndex: 'Kira Bedeli', key: 'KiraBedeli', align: 'right', render: (val) => formatMoney(val) },
        { title: 'Kabin Geliri', dataIndex: 'Kabin Bedeli', key: 'KabinBedeli', align: 'right', render: (val) => formatMoney(val) }
    ];
    
    const bankaData = prjId 
        ? Object.entries(bankaGruplari).map(([bankaAdi, veri], index) => ({
            key: index,
            sira: index + 1,
            bankaAdi,
            kabinAdedi: veri.kabin_adedi,
            toplamKira: veri.toplam_kira,
            toplamKabin: veri.toplam_kabin,
            toplamGelir: veri.toplam_kira + veri.toplam_kabin
        }))
        : banka_sozlesme_kayit_detaylari || [];
    
    const enflasyonOrani = Math.round((YILLIK_ENFLASYON[year] - 1) * 100);
    const artisOrani = Math.round((ARTIS_ORANLARI[year] - 1) * 100);
    
    return (
        <div className={darkMode ? 'mali-rapor-container dark-theme' : 'mali-rapor-container'}>
            <div className={darkMode ? 'mali-rapor-content dark-theme' : 'mali-rapor-content'}>
                <Title level={1} className={darkMode ? 'main-title dark-theme' : 'main-title'}>
                    Genel Mali Durum ve Karlılık Raporu
                </Title>
                <Title level={2} className={darkMode ? 'lokasyon-adi dark-theme' : 'lokasyon-adi'}>
                    {lokasyonAdi} (ID: {lokasyonId || 'N/A'})
                </Title>
                
                {/* Yıl Seçici */}
                <div className={darkMode ? 'year-selector' : 'year-selector dark-theme'}>
                    <label htmlFor="yearSelect">
                        <strong>Yıl Seçin ve Mali Prediksiyon Görün:</strong>
                    </label>
                    <Select
                        id="yearSelect"
                        value={currentYear}
                        onChange={(value) => setCurrentYear(value)}
                        style={{ width: 200, margin: '10px 0' }}
                    >
                        <Option value="2025">2025 (Baz Yıl)</Option>
                        <Option value="2026">2026 (Tahmini)</Option>
                        <Option value="2027">2027 (Tahmini)</Option>
                        <Option value="2028">2028 (Tahmini)</Option>
                        <Option value="2029">2029 (Tahmini)</Option>
                        <Option value="2030">2030 (Tahmini)</Option>
                    </Select>
                    <div className="prediction-info">
                        {year === 2025 ? (
                            <Text type="success">
                                {year} yılı baz alınmıştır. Gerçek veriler gösterilmektedir.
                            </Text>
                        ) : (
                            <div>
                                <Text>
                                    {year} yılı için tahmini <strong>%{enflasyonOrani}</strong> artış uygulandı.
                                </Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: '0.9em' }}>
                                    * Kabin Gelirleri, İnşaat/Kurulum ve İhale giderleri tek sefer olduğu için enflasyondan etkilenmez.
                                </Text>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Detaylı Gelir/Gider Analizi */}
                <Card title="Detaylı Gelir/Gider Analizi" className={darkMode ? 'section dark-theme' : 'section'}>
                    <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ marginBottom: '10px', color: '#007bff' }}>Kira Gelir/Gider Durumu</h4>
                        <div className="table-scroll">
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                                <tr>
                                    <td style={{ padding: '8px', border: '1px solid #d1d9e6' }}>Toplam Banka Geliri:</td>
                                    <td style={{ padding: '8px', border: '1px solid #d1d9e6', textAlign: 'right' }}>{formatMoney(tahminiKiraGeliri)}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px', border: '1px solid #d1d9e6' }}>Toplam YS Kira Gideri:</td>
                                    <td style={{ padding: '8px', border: '1px solid #d1d9e6', textAlign: 'right', color: '#c0392b' }}>{formatMoney(tahminiKiraGideri)}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px', border: '1px solid #d1d9e6', fontWeight: 'normal' }}>Net Kira Gelir/Gideri:</td>
                                    <td style={{ padding: '8px', border: '1px solid #d1d9e6', textAlign: 'right', fontWeight: 'normal' }}>
                                        <span 
                                            className={tahminiKiraNet >= 0 ? 'summary-value positive' : 'summary-value negative'}
                                            style={{ backgroundColor: darkMode ? '#1f1f1f' : undefined }}
                                        >
                                            {formatMoney(tahminiKiraNet)}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        </div>
                    </div>
                    <div>
                        <h4 style={{ marginBottom: '10px', color: '#007bff' }}>Kabin/Platform Gelir/Gider Durumu</h4>
                        <div className="table-scroll">
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                                <tr>
                                    <td style={{ padding: '8px', border: '1px solid #d1d9e6' }}>Toplam Kabin Geliri:</td>
                                    <td style={{ padding: '8px', border: '1px solid #d1d9e6', textAlign: 'right' }}>{formatMoney(tahminiKabinGeliri)}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px', border: '1px solid #d1d9e6' }}>Toplam İnşaat/Kurulum Maliyeti:</td>
                                    <td style={{ padding: '8px', border: '1px solid #d1d9e6', textAlign: 'right', color: '#c0392b' }}>{formatMoney(tahminiInsaatMaliyeti)}</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px', border: '1px solid #d1d9e6', fontWeight: 'normal' }}>Net Kabin/Platform Gelir/Gideri:</td>
                                    <td style={{ padding: '8px', border: '1px solid #d1d9e6', textAlign: 'right', fontWeight: 'normal' }}>
                                        <span 
                                            className={tahminiKabinNet >= 0 ? 'summary-value positive' : 'summary-value negative'}
                                            style={{ backgroundColor: darkMode ? '#1f1f1f' : undefined }}
                                        >
                                            {formatMoney(tahminiKabinNet)}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        </div>
                    </div>
                </Card>
                
                {/* Genel Karlılık */}
                <Card title="Genel Karlılık" className="section profitability-section">
                    <div className="table-scroll">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0' }}>Toplam Banka Geliri:</td>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0', textAlign: 'right', color: 'rgb(14, 173, 9)' }}>{formatMoney(tahminiBankaGeliri)}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0' }}>Toplam YS Kira Gideri:</td>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0', textAlign: 'right', color: '#c0392b' }}>{formatMoney(tahminiKiraGideri)}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0' }}>Toplam İnşaat/Kurulum Maliyeti:</td>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0', textAlign: 'right', color: '#c0392b' }}>{formatMoney(tahminiInsaatMaliyeti)}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0' }}>Genel Yönetim Gideri ({sozlesme_sayisi} Kabin için):</td>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0', textAlign: 'right', color: '#c0392b' }}>{formatMoney(tahminiYonetimGideri)}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0' }}>Personel Gideri ({sozlesme_sayisi} Kabin için):</td>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0', textAlign: 'right', color: '#c0392b' }}>{formatMoney(tahminiPersonelGideri)}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0' }}>Beklenmeyen Giderler ({sozlesme_sayisi} Kabin için):</td>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0', textAlign: 'right', color: '#c0392b' }}>{formatMoney(tahminiBeklenmeyen)}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0' }}>İhale Genel Gideri:</td>
                                <td style={{ padding: '8px', border: '1px solid #c0d0e0', textAlign: 'right', color: '#c0392b' }}>{formatMoney(tahminiIhaleGideri)}</td>
                            </tr>
                            <tr style={{ backgroundColor: '#fd7e14' }}>
                                <td style={{ backgroundColor: darkMode ? '#1f1f1f' : '#f0f8ff', padding: '8px', border: '1px solid #c0d0e0', fontWeight: 'normal', fontSize: '1.1em' }}>Net Gelir/Gider:</td>
                                <td style={{ backgroundColor: darkMode ? '#1f1f1f' : '#f0f8ff', padding: '8px', border: '1px solid #c0d0e0', textAlign: 'right', fontWeight: 'normal', fontSize: '1.1em' }}>
                                    <span 
                                        className={tahminiNetGelir >= 0 ? 'summary-value positive' : 'summary-value negative'}
                                        style={{ backgroundColor: darkMode ? '#1f1f1f' : undefined }}
                                    >
                                        {formatMoney(tahminiNetGelir)}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    </div>
                </Card>
                
                {/* Banka Sözleşme Detayları */}
                <Card title="Banka Sözleşme Detayları ve Gelirleri" className={darkMode ? 'section dark-theme' : 'section'}>
                    {banka_sozlesme_kayit_detaylari && banka_sozlesme_kayit_detaylari.length > 0 ? (
                        <>
                            <Table
                                columns={bankaColumns}
                                dataSource={bankaData}
                                pagination={false}
                                size="small"
                                scroll={{ x: 'max-content' }}
                                className={darkMode ? 'dark-table' : 'light-table'}
                            />
                            <div style={{ marginTop: '20px' }}>
                                <Text><strong>Kurulu Kabin:</strong> {lokasyon_banka_durum?.kabin || 0}</Text>
                                {' | '}
                                <Text><strong>Dolu Kabin:</strong> {(lokasyon_banka_durum?.kabin || 0) - (lokasyon_banka_durum?.boş || 0)}</Text>
                                {' | '}
                                <Text><strong>Boş Kabin:</strong> {lokasyon_banka_durum?.boş || 0}</Text>
                            </div>
                        </>
                    ) : (
                        <Text>Banka sözleşme kaydı bulunmamaktadır.</Text>
                    )}
                </Card>
                
                {/* Yer Sağlayıcı Kira Giderleri */}
                <Card title="Yer Sağlayıcı Kira Giderleri" className="section">
                    {Object.keys(kira_gideri_detay_liste).length > 0 ? (
                        <div className="details-condensed">
                            {Object.entries(kira_gideri_detay_liste).map(([key, value]) => (
                                <p key={key}>
                                    <p>{key}:</p>
                                    <span>
                                        {typeof value === 'number' 
                                            ? (key.toLowerCase().includes('sayısı') ? formatNumberTR(value, 0) : formatMoney(value))
                                            : value
                                        }
                                    </span>
                                </p>
                            ))}
                        </div>
                    ) : (
                        <Text>Yer Sağlayıcı sözleşme bilgisi bulunmamaktadır.</Text>
                    )}
                </Card>
                
                {/* İnşaat ve Kurulum Maliyetleri */}
                <Card title="İnşaat ve Kurulum Maliyetleri" className="section">
                    {Object.keys(gruplanmis_maliyetler).length > 0 ? (
                        <Table
                            className={darkMode ? 'dark-table' : 'table'}
                            scroll={{ x: 'max-content' }}
                            columns={[
                                { title: 'Masraf Tipi', dataIndex: 'tip', key: 'tip' },
                                { title: 'Toplam Tutar', dataIndex: 'tutar', key: 'tutar', align: 'right', render: (val) => formatMoney(val) }
                            ]}
                            dataSource={Object.entries(gruplanmis_maliyetler).map(([tip, tutar]) => ({
                                key: tip,
                                tip,
                                tutar
                            }))}
                            pagination={false}
                            size="small"
                            summary={() => (
                                <Table.Summary fixed>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} align="right">
                                            <p>TOPLAM İNŞAAT/KURULUM MALİYETİ:</p>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1} align="right">
                                            <p>{formatMoney(toplam_insaat_maliyeti)}</p>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </Table.Summary>
                            )}
                        />
                    ) : (
                        <Text>İnşaat veya kurulum maliyeti bulunmamaktadır.</Text>
                    )}
                </Card>
                
                {/* Yıllık Karlılık Analizi */}
                <Card title="💰 Yıllık Karlılık Analizi" className="section" id="yearlyProfitSection">
                    {(() => {
                        const yearlyData = [];
                        let totalCabinProfit = 0;
                        let totalRentalProfit = 0;
                        let totalOverallProfit = 0;
                        
                        for (let y = 2025; y <= year; y++) {
                            const artisCarpani = ARTIS_ORANLARI[y] || 1;
                            const kiraGeliri = (toplam_banka_geliri_sozlesme - toplam_kabin_geliri_bsk) * artisCarpani;
                            const kabinGeliri = (y === 2025) ? toplam_kabin_geliri_bsk : 0;
                            const kiraGideri = toplam_kira_gideri_hesaplanan * artisCarpani;
                            const insaatMaliyeti = (y === 2025) ? toplam_insaat_maliyeti : 0;
                            const cabinProfit = kabinGeliri - insaatMaliyeti;
                            const rentalProfit = kiraGeliri - kiraGideri;
                            const overallProfit = cabinProfit + rentalProfit;
                            
                            totalCabinProfit += cabinProfit;
                            totalRentalProfit += rentalProfit;
                            totalOverallProfit += overallProfit;
                            
                            yearlyData.push({
                                key: y,
                                year: y,
                                cabinProfit,
                                rentalProfit,
                                overallProfit
                            });
                        }
                        
                        return (
                            <Table
                                className={darkMode ? 'dark-table' : 'light-table'}
                                scroll={{ x: 'max-content' }}
                                columns={[
                                    { 
                                        title: <span style={{ color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined }}>Yıl</span>, 
                                        dataIndex: 'year', 
                                        key: 'year', 
                                        align: 'center',
                                        width: 100,
                                        render: (val) => (
                                            <p style={{ 
                                                backgroundColor: darkMode ? '#262626' : '#f8f9fa', 
                                                color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined,
                                                padding: '4px 8px', 
                                                borderRadius: '4px',
                                                margin: 0
                                            }}>
                                                {val}
                                            </p>
                                        )
                                    },
                                    { 
                                        title: <span style={{ color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined }}>Kira Karlılığı</span>, 
                                        dataIndex: 'rentalProfit', 
                                        key: 'rentalProfit', 
                                        align: 'center',
                                        render: (val) => (
                                            <span style={{ 
                                                color: val >= 0 
                                                    ? (darkMode ? '#73d13d' : 'rgb(14, 173, 9)') 
                                                    : (darkMode ? '#ff7875' : '#c0392b'), 
                                                fontWeight: 'normal' 
                                            }}>
                                                {formatMoney(val)}
                                            </span>
                                        )
                                    },
                                    { 
                                        title: <span style={{ color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined }}>Kabin Karlılığı</span>, 
                                        dataIndex: 'cabinProfit', 
                                        key: 'cabinProfit', 
                                        align: 'center',
                                        render: (val) => (
                                            <span style={{ 
                                                color: val >= 0 
                                                    ? (darkMode ? '#73d13d' : 'rgb(14, 173, 9)') 
                                                    : (darkMode ? '#ff7875' : '#c0392b'), 
                                                fontWeight: 'normal' 
                                            }}>
                                                {formatMoney(val)}
                                            </span>
                                        )
                                    },
                                    { 
                                        title: <span style={{ color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined }}>Toplam Net Kar/Zarar</span>, 
                                        dataIndex: 'overallProfit', 
                                        key: 'overallProfit', 
                                        align: 'center',
                                        render: (val) => (
                                            <span style={{ 
                                                color: val >= 0 
                                                    ? (darkMode ? '#73d13d' : 'rgb(14, 173, 9)') 
                                                    : (darkMode ? '#ff7875' : '#c0392b'), 
                                                fontWeight: 'normal',
                                                backgroundColor: darkMode ? '#25272b' : '#f0f8ff',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                display: 'inline-block'
                                            }}>
                                                {formatMoney(val)}
                                            </span>
                                        )
                                    }
                                ]}
                                dataSource={yearlyData}
                                pagination={false}
                                size="small"
                                summary={() => (
                                    <Table.Summary fixed>
                                        <Table.Summary.Row style={{ backgroundColor: darkMode ? '#262626' : undefined }}>
                                            <Table.Summary.Cell index={0} align="center" style={{ color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined }}>
                                                <p style={{ margin: 0, color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined }}>TOPLAM</p>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="center" style={{ color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined }}>
                                                <p style={{ 
                                                    margin: 0,
                                                    color: totalRentalProfit >= 0 
                                                        ? (darkMode ? '#73d13d' : 'rgb(14, 173, 9)') 
                                                        : (darkMode ? '#ff7875' : '#c0392b')
                                                }}>
                                                    {formatMoney(totalRentalProfit)}
                                                </p>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={2} align="center" style={{ color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined }}>
                                                <p style={{ 
                                                    margin: 0,
                                                    color: totalCabinProfit >= 0 
                                                        ? (darkMode ? '#73d13d' : 'rgb(14, 173, 9)') 
                                                        : (darkMode ? '#ff7875' : '#c0392b')
                                                }}>
                                                    {formatMoney(totalCabinProfit)}
                                                </p>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={3} align="center" style={{ color: darkMode ? 'rgba(255, 255, 255, 0.85)' : undefined }}>
                                                <p style={{ 
                                                    margin: 0,
                                                    color: totalOverallProfit >= 0 
                                                        ? (darkMode ? '#73d13d' : 'rgb(14, 173, 9)') 
                                                        : (darkMode ? '#ff7875' : '#c0392b')
                                                }}>
                                                    {formatMoney(totalOverallProfit)}
                                                </p>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                )}
                            />
                        );
                    })()}
                </Card>
            </div>
        </div>
    );
}

export default MaliRaporPage;

